import {
    BxfChatCanvasWidthEnum,
    BxfIconTypeEnum,
    WindowEventMap,
} from '@blk/bxf-web-components';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';

import { useDispatch } from 'react-redux';
import {
    activeContentPages,
    CHAT_BLK,
    CHAT_BLK_RESIZE_EVENT,
    ChatFrameConstants,
    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS,
    URIS,
    USED_CHAT_BLK_LOGIN_ENTRYPOINT,
} from '../constants';
import {
    useCache,
    useChatBlkStore,
    useHandleBxfEvent,
    useInitServices,
} from '../hooks';
import { useAuthContext, useChatBlkContext } from '../providers';
import { ChatBlkQueryService, ChatBlkTelemetryService } from '../services';
import { ChatBlkDispatch, querySlice, sendQuery } from '../store/store';
import { ChatblkMfeFunction, QueryState, QueryTypeEnum } from '../types';
import { getCanvasWidth, removeUserData, saveUserData, uuidv4 } from '../utils';

const {
    CHAT_SCROLL_CONTAINER,
    CANVAS_DISMISS_ASSISTIVE_TEXT,
    CANVAS_NEW_CHAT_ASSISTIVE_TEXT,
    CANVAS_FULL_SCREEN_ASSISTIVE_TEXT,
    CANVAS_EXIT_FULL_SCREEN_ASSISTIVE_TEXT,
    CANVAS_RECENT_ASSISTIVE_TEXT,
    CANVAS_CONTACT_RM_ASSISTIVE_TEXT,
} = ChatFrameConstants;
const {
    CHATBLK_MFE_FUNCTION_ACTION_MENU_ICON_CLICKED,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_CONTACT_RM_CLICKED,
    CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_FULLSCREEN_CLICKED,
    CHATBLK_MFE_FUNCTION_CLOSED,
    CHATBLK_MFE_FUNCTION_RECENT_CLICKED,
    CHATBLK_MFE_FUNCTION_RECENT_PROMPT_CLICKED,
    CHATBLK_MFE_FUNCTION_RESET_CLICKED,
    CHATBLK_MFE_FUNCTION_SIGN_IN_CLICKED,
    CHATBLK_MFE_FUNCTION_CANVAS_RESIZED,
    CHATBLK_MFE_FUNCTION_ACTION_CLOSE_ICON_CLICKED,
} = ChatblkMfeFunction;
const { RECENT, CHAT, ERROR, LOADING_ERROR, CONTACT_RM } = activeContentPages;

/**
 * Custom hook for managing the chat panel's behavior and interactions within the application.
 * @param chatCanvasRef - A reference to the chat canvas element used to adjust its size and retrieve shadow DOM elements.
 * @param hostHeaderRef - A reference to the host header element, which is used to calculate layout offsets for the canvas.
 *
 * @returns An object containing state variables and event handler functions for managing:
 * @example
 * const {
 *   activeContent,
 *   handleLoginClicked,
 *   headerActionButtonConfig,
 *   handleContentClosed,
 * } = useChatPanelFeature(chatCanvasRef, hostHeaderRef);
 */
const useChatPanelFeature = (
    chatCanvasRef: React.RefObject<HTMLBxfChatCanvasElement>,
    hostHeaderRef: React.RefObject<HTMLElement>,
    wrapper?: HTMLElement
) => {
    const { cachedPanelWidth } = useCache();

    const chatService = ChatBlkQueryService.getInstance();
    const { promptBoxRef, close } = useChatBlkContext();
    const {
        activeContent,
        mostRecentQueryId,
        isFullscreen,
        isResetDialogOpen,
        setIsFullscreen,
        setActiveContent,
        recentPrompts,
        errorMessage,
        setIsResetDialogOpen,
        setErrorMessage,
        setPanelWidth,
        clearAllQueries,
        // setCurrentQueryId,
        // setIsQueryCanceled,
        // setIsProgressiveLoading,
        isPanelOpen,
        setPaddingHeight,
        queries,
        setHasRecentPromptsStyling,
    } = useChatBlkStore();
    const { login } = useAuthContext();
    const { isEmbedded } = useInitServices();
    const dispatch = useDispatch<ChatBlkDispatch>();
    const { storeCache } = useCache();
    const initialWidth = useRef(BxfChatCanvasWidthEnum.LMS);
    const cachedScrollTop = useRef(0);
    const chatContentEl = useRef<HTMLDivElement | null>(null);
    const recentPromptContentEl = useRef<HTMLDivElement | null>(null);
    const hasQuery = useMemo<boolean>(
        () =>
            !!queries?.ids?.length ||
            queries.ids.some(
                (query) => queries.entries[query]?.state !== QueryState.CANCELED
            ),
        [queries.ids]
    );
    const closePopovers = useCallback(() => {
        const popoverEls =
            chatCanvasRef.current?.querySelectorAll('bxf-popover');

        popoverEls?.forEach(async (popoverEl) => {
            await popoverEl.close();
            popoverEl.isOpen = false;
        });
    }, [chatCanvasRef]);

    useEffect(() => {
        // Temporary work around to disable pointer events and user selection
        // on the iframe when the panel is open.
        // This is to prevent continuous resizing on 'financial-professionals/dashboard'
        const iframeR0: HTMLElement = document?.querySelector(
            'iframe[id="iframe-r0"]'
        );
        if (!iframeR0) return;
        Object.assign(
            iframeR0.style,
            isPanelOpen
                ? {
                      pointerEvents: 'none',
                      userSelect: 'none',
                  }
                : {
                      pointerEvents: 'auto',
                      userSelect: 'auto',
                  }
        );
    }, [isPanelOpen]);

    useLayoutEffect(() => {
        const chatCanvas = chatCanvasRef.current; // Have to hardcode the threshold for the mobile header
        // in AC because it is not a header semantically
        const hostHeader =
            document.querySelector(
                '.blk-ac-container-fluid.container-for-header'
            ) || hostHeaderRef.current;
        if (!chatCanvas || !wrapper) return;

        const setCanvasHeight = () => {
            chatCanvas.style.height = `calc(100vh - ${Math.max(
                0,
                hostHeader?.getBoundingClientRect().bottom || 0
            )}px)`;
            wrapper.style.top = `${Math.max(
                0,
                hostHeader?.getBoundingClientRect().bottom || 0
            )}px`;
            closePopovers();
        };

        window.addEventListener(WindowEventMap.scroll, setCanvasHeight, {
            passive: true,
        });
        const resizeObserver = new ResizeObserver(setCanvasHeight);
        resizeObserver.observe(document.body);

        return () => {
            window.removeEventListener(WindowEventMap.scroll, setCanvasHeight);
            resizeObserver.disconnect();
        };
    }, [chatCanvasRef, hostHeaderRef, isPanelOpen]);

    useEffect(() => {
        const { scroll } = WindowEventMap;

        // Prevent the scroll event from propagating to the document and causing unwanted behavior
        const scrollHandler = (e: Event) => {
            e.stopPropagation();
            const { scrollTop } = e.target as HTMLDivElement;
            cachedScrollTop.current = scrollTop;
            closePopovers();
        };
        const wheelHandler = (e: WheelEvent, isMain = true) => {
            const { scrollTop, scrollHeight, clientHeight } = isMain
                ? (chatContentEl.current as HTMLElement)
                : (recentPromptContentEl.current as HTMLElement);

            const isAtTop = scrollTop === 0;
            const isAtBottom =
                scrollHeight - Math.ceil(scrollTop) <= clientHeight;

            if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
                e.preventDefault();
                e.stopPropagation();
            }
        };

        const recentWheelHandler = (e: WheelEvent) => {
            wheelHandler(e, false);
        };

        if (activeContent === activeContentPages.CHAT) {
            // If the active content is chat and cached scroll top exists, scroll to that position
            setTimeout(() => {
                chatContentEl.current =
                    chatCanvasRef.current.shadowRoot.querySelector(
                        CHAT_SCROLL_CONTAINER
                    );
                chatContentEl.current?.scrollTo({
                    top: cachedScrollTop.current,
                    behavior: 'instant',
                });
                recentPromptContentEl.current =
                    chatCanvasRef.current.shadowRoot.querySelector(
                        '.bxf-chat-canvas__recent-prompts'
                    );

                chatContentEl.current?.addEventListener(scroll, scrollHandler);
                chatContentEl.current?.addEventListener('wheel', wheelHandler, {
                    passive: false,
                });
                recentPromptContentEl.current?.addEventListener(
                    'wheel',
                    recentWheelHandler,
                    {
                        passive: false,
                    }
                );
            }, 10);
        } else {
            recentPromptContentEl.current = null;
        }

        return () => {
            chatContentEl.current?.removeEventListener(scroll, scrollHandler);
            chatContentEl.current?.removeEventListener('wheel', wheelHandler);
            recentPromptContentEl.current?.removeEventListener(
                'wheel',
                recentWheelHandler
            );
        };
    }, [activeContent, recentPromptContentEl.current]);

    useLayoutEffect(() => {
        // Set the initial width of the chat canvas. Prevents initial width from being too large
        // if logs back in with a smaller screen size.
        const panelWidth = Math.min(
            activeContent !== 'login' && cachedPanelWidth()
                ? cachedPanelWidth()
                : getCanvasWidth(window.innerWidth),
            window.innerWidth - 20
        );
        initialWidth.current = panelWidth;
        setPanelWidth(panelWidth);
    }, [activeContent, chatCanvasRef]);

    const headerActionButtonConfig = useMemo(
        () => [
            {
                icon: 'bxf-contact' as BxfIconTypeEnum,
                label: CANVAS_CONTACT_RM_ASSISTIVE_TEXT,
                contentSlotName: CONTACT_RM,
                dataLinkEvent:
                    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS['bxf-contact'],
                onPressCallback: () => {
                    setActiveContent(
                        activeContent === CONTACT_RM ? CHAT : CONTACT_RM
                    );
                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_CONTACT_RM_CLICKED
                    );
                },
            },
            {
                icon: 'bxf-new-chat' as BxfIconTypeEnum,
                isDisabled: !hasQuery,
                label: CANVAS_NEW_CHAT_ASSISTIVE_TEXT,
                onPressCallback: () => {
                    setIsResetDialogOpen(!isResetDialogOpen);
                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_ACTION_MENU_ICON_CLICKED,
                        { from: activeContent }
                    );
                },
            },
            {
                icon: 'bxf-history' as BxfIconTypeEnum,
                isDisabled: !recentPrompts?.length,
                label: CANVAS_RECENT_ASSISTIVE_TEXT,
                shouldShowIconCallback: () =>
                    chatCanvasRef.current?.getBoundingClientRect().width < 1024,
                contentSlotName: RECENT,
                dataLinkEvent:
                    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS['bxf-history'],
                onPressCallback: async () => {
                    setActiveContent(activeContent === RECENT ? CHAT : RECENT);
                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_RECENT_CLICKED
                    );
                },
            },
            {
                icon: 'bxf-full-screen' as BxfIconTypeEnum,
                label: CANVAS_FULL_SCREEN_ASSISTIVE_TEXT,
                dataLinkEvent:
                    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS['bxf-full-screen'],
                shouldShowIconCallback: () => isEmbedded && !isFullscreen,
                onPressCallback: () => {
                    setIsFullscreen(true);
                    setActiveContent(CHAT);
                    setHasRecentPromptsStyling(true);
                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_FULLSCREEN_CLICKED,
                        { from: activeContent, isFullscreen: true }
                    );
                },
            },
            {
                icon: 'bxf-exit-full-screen' as BxfIconTypeEnum,
                label: CANVAS_EXIT_FULL_SCREEN_ASSISTIVE_TEXT,
                dataLinkEvent:
                    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS[
                        'bxf-exit-full-screen'
                    ],
                shouldShowIconCallback: () => isEmbedded && isFullscreen,
                onPressCallback: async () => {
                    await chatCanvasRef.current?.setCanvasWidth(
                        cachedPanelWidth()
                    );
                    setIsFullscreen(false);
                    setHasRecentPromptsStyling(false);

                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_ACTION_MENU_OPTION_FULLSCREEN_CLICKED,
                        { from: activeContent, isFullscreen: false }
                    );
                },
            },
            {
                icon: 'bxf-dismiss' as BxfIconTypeEnum,
                label: CANVAS_DISMISS_ASSISTIVE_TEXT,
                dataLinkEvent:
                    MENU_ACTION_BUTTONS_DATA_LINK_EVENTS['bxf-dismiss'],
                onPressCallback: () => {
                    close();
                    ChatBlkTelemetryService.track(CHATBLK_MFE_FUNCTION_CLOSED, {
                        from: activeContent,
                        isFullscreen: true,
                    });
                    ChatBlkTelemetryService.track(
                        CHATBLK_MFE_FUNCTION_ACTION_MENU_ICON_CLICKED,
                        { type: 'bxf-dismiss' }
                    );
                },
            },
        ],
        [
            activeContent,
            queries,
            recentPrompts,
            isEmbedded,
            isFullscreen,
            setActiveContent,
            setIsResetDialogOpen,
            setIsFullscreen,
            setHasRecentPromptsStyling,
            chatCanvasRef,
            recentPrompts.length,
            isResetDialogOpen,
            cachedPanelWidth,
        ]
    );

    const handleLoginClicked = async () => {
        storeCache([{ key: USED_CHAT_BLK_LOGIN_ENTRYPOINT, value: 'true' }]);
        await chatService.requestUserLogin();
        if (URIS.PRE_PROD_HOSTS.includes(window.location.hostname)) {
            setActiveContent(activeContentPages.LOADING);
        }
        ChatBlkTelemetryService.track(CHATBLK_MFE_FUNCTION_SIGN_IN_CLICKED);
        chatService.isLoggedIn && (await login());
    };

    const handleErrorResponse = useCallback(async () => {
        if (activeContent === LOADING_ERROR) {
            const iframe = document.getElementById(
                'chatblk-iframe'
            ) as HTMLIFrameElement;
            iframe
                ? iframe.contentWindow.location.reload()
                : window.location.reload();
        } else {
            const query = queries.entries?.[mostRecentQueryId];
            await promptBoxRef?.current.setValue('');
            dispatch(
                sendQuery({
                    prompt: query?.query || '',
                    chatExchangeId: uuidv4(),
                    hashId: uuidv4(),
                    queryType: QueryTypeEnum.QUERY,
                })
            );
            setErrorMessage(null);
        }
    }, [activeContent, mostRecentQueryId]);

    /**
     * Handles the closure of the current content panel.
     *
     * @returns A promise that resolves once the closure handling is complete
     */
    const handleContentClosed = useCallback(async () => {
        setActiveContent(CHAT);
        if (activeContent === ERROR) {
            await promptBoxRef?.current.setValue(
                queries.entries[mostRecentQueryId].query
            );
            setTimeout(async () => {
                await promptBoxRef?.current.calculateTextBoxHeight();
                dispatch(querySlice.actions.removeQuery(mostRecentQueryId));
                setErrorMessage(null);
                // reset to avoid awkward extra space at the bottom when removing the most recent query
                setPaddingHeight(0);
            }, 10);
            ChatBlkTelemetryService.track(
                CHATBLK_MFE_FUNCTION_ACTION_CLOSE_ICON_CLICKED,
                { from: activeContent, to: CHAT }
            );
        }
    }, [activeContent, mostRecentQueryId, promptBoxRef]);

    const handlePanelOnResizing = (event: CustomEvent) => {
        const { panelWidth } = event.detail;

        // Safari handler for disabling text selection
        document.body.style['-webkit-user-select'] = 'none';

        if (activeContent === RECENT && panelWidth < 1024) {
            setActiveContent(CHAT);
        }
        promptBoxRef?.current.calculateTextBoxHeight();
        setHasRecentPromptsStyling(panelWidth > 1027);
        setPanelWidth(panelWidth);
    };

    const handlePanelResized = (event: CustomEvent) => {
        const { panelWidth = 'initial' } = event.detail;
        console.log(`[${CHAT_BLK}] ${CHAT_BLK_RESIZE_EVENT}`, event.detail);
        const currentUserId = chatService.username;

        // Safari handler for enabling text selection after stop resizing
        document.body.style['-webkit-user-select'] = 'auto';

        if (chatService.isLoggedIn) {
            saveUserData(currentUserId, 'settings', {
                panelWidth,
                fontSize: 'default',
            });
        } else {
            removeUserData(currentUserId, 'settings');
        }

        ChatBlkTelemetryService.track(CHATBLK_MFE_FUNCTION_CANVAS_RESIZED, {
            panelWidth,
        });
    };

    const handleRecentPromptClicked = async ({
        detail: { prompt },
    }: CustomEvent) => {
        setActiveContent(CHAT);
        dispatch(
            sendQuery({
                prompt,
                chatExchangeId: uuidv4(),
                hashId: uuidv4(),
                queryType: QueryTypeEnum.QUERY,
            })
        );
        // setCurrentQueryId(''); // Reset the current query ID
        // setIsQueryCanceled(false); // Reset the query canceled state
        // // setIsProgressiveLoading(true); // TODO: Set progressive loading to true
        // setIsProgressiveLoading(false);
        ChatBlkTelemetryService.track(
            CHATBLK_MFE_FUNCTION_RECENT_PROMPT_CLICKED,
            { prompt }
        );
    };

    const handleResetClicked = useHandleBxfEvent(() => {
        // setCurrentQueryId(''); // Reset the current query ID
        // setIsQueryCanceled(false); // Reset the query canceled state
        // setIsProgressiveLoading(true); // TODO: Set progressive loading to true
        // setIsProgressiveLoading(false);
        setIsResetDialogOpen(true);
        clearAllQueries();
    }, CHATBLK_MFE_FUNCTION_RESET_CLICKED);

    return {
        hasQuery: Boolean(queries?.ids?.length ?? 0 > 0),
        activeContent,
        chatCanvasRef,
        isPanelOpen,
        isFullscreen,
        initialWidth,
        errorMessage,
        headerActionButtonConfig,
        recentPrompts,
        handleLoginClicked,
        handleResetClicked,
        handleErrorResponse,
        handleContentClosed,
        handlePanelOnResizing,
        handleRecentPromptClicked,
        handlePanelResized,
    };
};

export default useChatPanelFeature;
